<template>
  <AnnotationToolViewingMode3DControls
    :show="true"
    @top-view="handleTopView"
    @front-view="handleFrontView"
    @perspective-camera="switchToPerspectiveCamera"
    @orthographic-camera="switchToOrthographicCamera"
  />
  <TresCanvas
    ref="canvas"
    v-bind="gl"
    render-mode="on-demand"
  >
    <TresPerspectiveCamera
      v-if="cameraType === 'perspective'"
      ref="cameraPerspective"
      :position="cameraPerspectivePosition"
      :up="upDirection"
      :fov="45"
      :look-at="lookAt"
    />
    <TresOrthographicCamera
      v-else-if="cameraType === 'orthographic'"
      ref="cameraOrthographic"
      :up="upDirection"
      :position="cameraOrthographicPosition"
    />
    <OrbitControls
      ref="controls"
      make-default
      :enableDamping="true"
      :dampingFactor="1"
    />
    <TransformControls
      v-if="transformEnabled"
      ref="transformControls"
      :object="transformObject"
      :mode="transformMode"
      @mouse-up="handleTransformMouseUp(transformObject)"
    />
    <TresGroup v-if="showTypes?.pcd" ref="pointArrayGroup">
      <primitive
        v-for="pointObj in processPcd(pointArray)"
        :key="pointObj.annotation.id"
        :object="pointObj.pointsBuffer"
      />
    </TresGroup>

    <TresGroup v-if="showTypes?.pcd" ref="pointGroup">
      <primitive
        v-for="pointObj in processPcd(points)"
        :key="pointObj.annotation.id"
        :object="pointObj.pointsBuffer"
      />
    </TresGroup>

    <TresGroup v-if="showTypes?.lidar" ref="lidarGroup">
      <primitive
        v-for="lidarObj in processLidar(lidar)"
        :key="lidarObj.annotation.id"
        :object="lidarObj.pointsBuffer"
      />
    </TresGroup>

    <TresGroup
      v-if="showTypes?.['3dbox']"
      ref="boxGroup"
      :invalidateBoxes="invalidateBoxes"
    >
      <TresMesh
        v-for="(box, boxIndex) in boxes3d"
        :ref="(object) => boxRefsFn(object, boxIndex, box)"
        :key="box.id"
        :position="[box.data_json.x, box.data_json.y, box.data_json.z]"
        @click="(el, intersection, pointerEvent) => handleBoxClicked(box, el, intersection, pointerEvent)"
        @pointer-enter="(el) => handleBoxPointerEnter(el)"
        @pointer-leave="(object) => handleBoxPointerLeave(object)"
      >
        <TresBoxGeometry :args="[box.data_json.dx, box.data_json.dy, box.data_json.dz, 1, 1, 1]" />
        <primitive v-if="!invalidateBoxes" :key="`top-${box.id}-${selectedAnnotationIdentifiers.includes(box.id)}`" :object="getBoxTopEdges(box.data_json.dx, box.data_json.dy, box.data_json.dz, box.label_index, 0.003, selectedAnnotationIdentifiers.includes(box.id))" />
        <primitive v-if="!invalidateBoxes" :key="`bottom-${box.id}-${selectedAnnotationIdentifiers.includes(box.id)}`" :object="getBoxBottomEdges(box.data_json.dx, box.data_json.dy, box.data_json.dz, box.label_index, 0.003, selectedAnnotationIdentifiers.includes(box.id))" />
        <primitive v-if="!invalidateBoxes" :key="`left-${box.id}-${selectedAnnotationIdentifiers.includes(box.id)}`" :object="getBoxLeftEdges(box.data_json.dx, box.data_json.dy, box.data_json.dz, box.label_index, 0.003, selectedAnnotationIdentifiers.includes(box.id))" />
        <primitive v-if="!invalidateBoxes" :key="`right-${box.id}-${selectedAnnotationIdentifiers.includes(box.id)}`" :object="getBoxRightEdges(box.data_json.dx, box.data_json.dy, box.data_json.dz, box.label_index, 0.003, selectedAnnotationIdentifiers.includes(box.id))" />
        <TresLineDashedMaterial
          :color="selectedAnnotationIdentifiers.includes(box.id) ? '#FFFFFF' : getLabelColor(box.label_index)"
          :wireframe="false"
          :transparent="true"
          :opacity="0.25"
        />
      </TresMesh>
    </TresGroup>
    <TresAmbientLight :intensity="1" />
    <TresGridHelper
      ref="grid"
      :args="[100, 100]"
      :rotateX="Math.PI/2"
    />
    <TresAxesHelper :args="[10]" />
  </TresCanvas>
</template>

<script setup>
import AnnotationToolViewingMode3DControls from '@/components/DatasetComponent/AnnotationTool/AnnotationToolViewingMode3DControls.vue';
import { TresCanvas } from '@tresjs/core';
import { OrbitControls, TransformControls } from '@tresjs/cientos';
import {
  ref, reactive, shallowRef, computed, onMounted, onUnmounted, watch, toRefs, nextTick,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import usePCD from '@/composables/annotationTool/usePCD.js';
import useAnnotationColorMap from '@/composables/useAnnotationColorMap.js';
import * as THREE from 'three';
import gsap from 'gsap';
import { v4 as uuidv4 } from 'uuid';
import { getAnnotationTypes } from '@/assets/js/utils.js';
import useColorParser from '@/composables/useColorParser.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';

const props = defineProps({
  imageObj: { type: Object, default: () => {} },
  annotations: { type: Array, default: () => [] },
  pendingAnnotations: { type: Array, default: () => [] },
  updatedAnnotations: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
  selectedLabel: { type: Object, default: null },
  selectedLabels: { type: Object, default: null },
  selectedAnnotationSets: { type: Array, default: () => [] },
  isAuditMode: { type: Boolean, default: false },
  points: { type: Array, default: null },
  lidar: { type: Array, default: null },
  currentTool: { type: String, default: null },
  selectedAnnotationIdentifiers: { type: Array, default: () => [] },
  showTypes: { type: Object, default: () => {} },
  editingEnabled: { type: Boolean, default: false },
});
const {
  imageObj,
  annotations,
  pendingAnnotations,
  labels,
  selectedLabel,
  selectedLabels,
  selectedAnnotationSets,
  isAuditMode,
  points,
  lidar,
  currentTool,
  selectedAnnotationIdentifiers,
  showTypes,
  editingEnabled,
} = toRefs(props);

const emit = defineEmits(['update:updatedAnnotations', 'create-annotation', 'update:selectedAnnotationIdentifiers']);

const {
  pointArray,
  pcdAnnotations,
  parsePCDFileAsPointsBuffer,
  setPointsMaterial,
  cloneLidarPointsBuffer,
  clonePcdPointsBuffer,
} = usePCD();

const {
  parseColor,
} = useColorParser();

const gl = reactive({
  clearColor: '#2B3846',
  shadows: true,
  alpha: false,
  shadowMapType: THREE.BasicShadowMap,
  outputEncoding: THREE.SRGBColorSpace,
  toneMapping: THREE.NoToneMapping,
});

const canvas = ref(null);
const grid = ref(null);
const cameraPerspective = shallowRef(null);
const cameraOrthographic = shallowRef(null);
const controls = ref(null);
const transformControls = ref(null);
const scene = ref(null);
const renderer = ref(null);
const raycaster = ref(null);
const cameraType = ref('perspective');
const cameraPerspectivePosition = ref([-5, 5, 5]);
const cameraOrthographicPosition = ref([-10, 0, 0]);
const upDirection = [0, 0, 1];
const lookAt = ref([0, 0, 0]);
const transformObject = ref(null);
const transformMode = ref('translate');
const transformEnabled = ref(false);
const selectedObjects = ref({
  boxes: [],
});

const boxRefs = shallowRef([]);
const boxRefsFn = (object, boxIndex, box) => {
  if (object) {
    object.userData.annotation = box;
    boxRefs.value[boxIndex] = object;
  } else {
    boxRefs.value.splice(boxIndex, 1);
  }
};
const invalidateBoxes = ref(false);

const currentCamera = computed(() => {
  if (cameraType.value === 'perspective') {
    return cameraPerspective.value;
  } else if (cameraType.value === 'orthographic') {
    return cameraOrthographic.value;
  }
  return cameraPerspective.value;
});

const boxes3d = computed(() => [
  ...annotations.value.filter((anno) => {
    const isLabelSelected = !selectedLabels.value || selectedLabels.value?.includes(anno.label_index);
    const isAnnotationSetSelected = !selectedAnnotationSets.value || selectedAnnotationSets.value?.includes(anno.annotation_set_id);
    return getAnnotationTypes(anno).includes('box3d') && isLabelSelected && (isAnnotationSetSelected || isAuditMode.value);
  }),
  ...pendingAnnotations.value.filter((anno) => getAnnotationTypes(anno).includes('box3d') && anno.image_id === imageObj.value?.id),
]);

function processLidar(lidarToProcess) {
  if (!lidarToProcess) return [];

  return lidarToProcess.map((item) => {
    // Create a fresh copy of the lidar data
    const clonedItem = {
      annotation: item.annotation,
      // Make a deep copy of the original data
      pointsBuffer: cloneLidarPointsBuffer(item.positions, item.colors),
    };
    return clonedItem;
  });
}

// Add a computed property to create fresh copies of pcd data. Needed because tres.js clears the geometry when disposed.
// Remove if tres version updated
function processPcd(pcdToProcess) {
  if (!pcdToProcess) return [];

  return pcdToProcess.map((item) => {
    // Create a fresh copy of the points data
    const clonedItem = {
      annotation: item.annotation,
      // Make a deep copy of the original data
      pointsBuffer: clonePcdPointsBuffer(item.positions, item.colors),
    };
    return clonedItem;
  });
}

watch(labels, () => {
  deselectAllBoxes();
  invalidateBoxes.value = true;
  nextTick(() => {
    invalidateBoxes.value = false;
  });
});

watch(annotations, async (newAnnotations) => {
  if (newAnnotations) {
    pcdAnnotations.value = newAnnotations.filter((anno) => anno.type === "pcd");
  }
});

watch(pcdAnnotations, async () => {
  getPointArray();
});

watch(currentTool, async (newTool) => {
  if (!editingEnabled.value) return;

  if (['translate-object', 'scale-object'].includes(currentTool.value)) {
    if (transformEnabled.value && transformObject.value) {
      if (currentTool.value === 'translate-object') transformMode.value = 'translate';
      if (currentTool.value === 'scale-object') transformMode.value = 'scale';
    }
    if (selectedObjects.value.boxes.length > 0) {
      const selectedBox = boxRefs.value.find((boxRef) => boxRef.userData.selected);
      if (selectedBox) {
        transformObject.value = selectedBox;
        transformEnabled.value = true;
      }
    }
  } else {
    transformEnabled.value = false;
    transformObject.value = null;
  }
});

watch(imageObj, (newImageObj) => {
  transformEnabled.value = false;
  transformObject.value = null;
});

watch(selectedAnnotationIdentifiers, () => {
  // Derive local 3D box selection from the shared cross-viewer selection list (2D canvas ids included).
  selectedObjects.value.boxes = boxes3d.value.filter((box) => selectedAnnotationIdentifiers.value.includes(box.id));
  updateBoxSelection();
}, { immediate: true });

onMounted(async () => {
  window.addEventListener('keyup', handleKeyEvent);

  renderer.value = canvas.value.context.renderer.value;
  renderer.value.setPixelRatio(window.devicePixelRatio);
  scene.value = canvas.value.context.scene.value;
  raycaster.value = canvas.value.context.raycaster.value;
  pcdAnnotations.value = annotations.value.filter((anno) => anno.type === "pcd");
  getPointArray();

  enableCanvasClick();
});

onUnmounted(() => {
  window.removeEventListener('keyup', handleKeyEvent);
});

function handleKeyEvent(event) {
  if (event.key === 'Delete') {
    if (!editingEnabled.value) return;

    deleteObjects();
  }
}

async function readFile(annotation) {
  const dataConnect = new DatastoreConnect();
  let data;
  if (annotation.is_sensor) {
    data = await dataConnect.getSensorFile(annotation.id, 'arrayBuffer')
      .catch((error) => {
        console.log(error);
        return null;
      });
  } else {
    data = await dataConnect.getAnnotationFile(annotation.id, 'arrayBuffer')
      .catch((error) => {
        console.log(error);
        return null;
      });
  }
  return data;
}

async function getPointArray() {
  // Reset point array for each annotation
  pointArray.value = [];
  if (!points.value && pcdAnnotations.value.length > 0) {
    for (const annotation of pcdAnnotations.value) {
      if (annotation.polygon && annotation.polygon !== "") {
        // Convert polygon to ArrayBuffer
        const textEncoder = new TextEncoder();
        const arrayBuffer = textEncoder.encode(annotation.polygon).buffer;
        annotation.data = arrayBuffer;
        // Process points buffer and material
        const pointsBuffer = shallowRef(null);
        pointsBuffer.value = parsePCDFileAsPointsBuffer(annotation.data);
        setPointsMaterial(pointsBuffer.value.material);
        // Add annotation and pointsBuffer to pointArray
        pointArray.value.push({
          annotation,
          pointsBuffer,
        });
      } else {
        const arrayBuffer = await readFile(annotation);
        if (arrayBuffer) {
          annotation.data = arrayBuffer;
          // Process points buffer and material
          const pointsBuffer = shallowRef(null);
          pointsBuffer.value = parsePCDFileAsPointsBuffer(annotation.data);
          setPointsMaterial(pointsBuffer.value.material);
          // Add annotation and pointsBuffer to pointArray
          pointArray.value.push({
            annotation,
            pointsBuffer,
          });
        }
      }
    }
  } else {
    pointArray.value = [];
  }
}

function handleBoxClicked(box, el, intersection, pointerEvent) {
  if (currentTool.value === '3d-pointer') {
    if (el.object.geometry.type === 'BoxGeometry') {
      const isCtrlPressed = Boolean(pointerEvent?.ctrlKey || pointerEvent?.metaKey);
      if (isCtrlPressed) {
        if (selectedAnnotationIdentifiers.value.includes(box.id)) {
          deselectBox(box);
        } else {
          selectBox(el.object, box);
        }
      } else {
        deselectAllBoxes();
        selectBox(el.object, box);
      }
    }
  }

  if (['translate-object', 'scale-object'].includes(currentTool.value)) {
    if (!editingEnabled.value) return;

    if (currentTool.value === 'translate-object') transformMode.value = 'translate';
    if (currentTool.value === 'scale-object') transformMode.value = 'scale';

    deselectAllBoxes();
    selectBox(el.object, box);
    transformObject.value = el.object;
    transformEnabled.value = true;
  }

  if (currentTool.value === '3d-edit-label') {
    if (!editingEnabled.value) return;

    el.object.userData.annotation.label_index = selectedLabel.value.index;
    el.object.userData.annotation.label_id = selectedLabel.value.id;
    el.object.userData.annotation.label_name = selectedLabel.value.name;
    const material = el.object.material.clone();
    material.color.set(getLabelColor(selectedLabel.value?.index));

    el.object.material = material;
    const lineMaterial = el.object.children[0].material.clone();
    lineMaterial.color.set(getLabelColor(selectedLabel.value?.index));
    el.object.children[0].material = lineMaterial;
    emit('update:updatedAnnotations', JSON.parse(JSON.stringify(annotations.value)));
  }
}

// Handle point colors
const { map: labelColorMap } = useAnnotationColorMap({ items: labels, key: 'index' });
function getLabelColor(index) {
  if (index !== undefined && index !== null) {
    const targetLabel = labels.value.find((e) => e.index === index);
    if (targetLabel && targetLabel.color) {
      const parsed = parseColor(targetLabel.color);
      // Only strip an alpha suffix when present (#RRGGBBAA); leave plain #RRGGBB untouched.
      return parsed.length === 9 ? parsed.slice(0, -2) : parsed;
    } else if (labelColorMap.value[index]) {
      return labelColorMap.value[index];
    }
  }
  return '#250E81';
}

function getBoxTopEdges(dx, dy, dz, label, lineWidth = 0.001, isSelected = false) {
  const vertices = [
    // Front face edges
    [-dx / 2, -dy / 2, dz / 2],
    [dx / 2, -dy / 2, dz / 2],
    [dx / 2, dy / 2, dz / 2],
    [-dx / 2, dy / 2, dz / 2],
    [-dx / 2, -dy / 2, dz / 2],
  ];

  const lineGeometry = new LineGeometry();
  lineGeometry.setPositions(vertices.flat());

  const lineMaterial = new LineMaterial({
    color: isSelected ? 0xFFFFFF : getLabelColor(label),
    linewidth: lineWidth,
  });

  const line = new Line2(lineGeometry, lineMaterial);
  return line;
}

function getBoxBottomEdges(dx, dy, dz, label, lineWidth = 0.001, isSelected = false) {
  const vertices = [
    // Front face edges
    [-dx / 2, -dy / 2, -dz / 2],
    [dx / 2, -dy / 2, -dz / 2],
    [dx / 2, dy / 2, -dz / 2],
    [-dx / 2, dy / 2, -dz / 2],
    [-dx / 2, -dy / 2, -dz / 2],
  ];

  const lineGeometry = new LineGeometry();
  lineGeometry.setPositions(vertices.flat());

  const lineMaterial = new LineMaterial({
    color: isSelected ? 0xFFFFFF : getLabelColor(label),
    linewidth: lineWidth,
  });

  const line = new Line2(lineGeometry, lineMaterial);
  return line;
}

function getBoxLeftEdges(dx, dy, dz, label, lineWidth = 0.001, isSelected = false) {
  const vertices = [
    // Front face edges
    [-dx / 2, dy / 2, -dz / 2],
    [dx / 2, dy / 2, -dz / 2],
    [dx / 2, dy / 2, dz / 2],
    [-dx / 2, dy / 2, dz / 2],
    [-dx / 2, dy / 2, -dz / 2],
  ];

  const lineGeometry = new LineGeometry();
  lineGeometry.setPositions(vertices.flat());

  const lineMaterial = new LineMaterial({
    color: isSelected ? 0xFFFFFF : getLabelColor(label),
    linewidth: lineWidth,
  });

  const line = new Line2(lineGeometry, lineMaterial);
  return line;
}

function getBoxRightEdges(dx, dy, dz, label, lineWidth = 0.001, isSelected = false) {
  const vertices = [
    // Front face edges
    [-dx / 2, -dy / 2, -dz / 2],
    [dx / 2, -dy / 2, -dz / 2],
    [dx / 2, -dy / 2, dz / 2],
    [-dx / 2, -dy / 2, dz / 2],
    [-dx / 2, -dy / 2, -dz / 2],
  ];

  const lineGeometry = new LineGeometry();
  lineGeometry.setPositions(vertices.flat());

  const lineMaterial = new LineMaterial({
    color: isSelected ? 0xFFFFFF : getLabelColor(label),
    linewidth: lineWidth,
  });

  const line = new Line2(lineGeometry, lineMaterial);
  return line;
}

async function handleTopView() {
  controls.value.value.enableDamping = false;
  controls.value.value.enabled = false;
  gsap.to(currentCamera.value.position, {
    duration: 1,
    x: 0,
    y: 0,
    z: 20,
    ease: 'power3.out',
    onComplete: () => {
      controls.value.value.enableDamping = true;
      controls.value.value.enabled = true;
    },
  });
  gsap.to(currentCamera.value.up, {
    delay: 0.1,
    duration: 1,
    x: 1,
    y: 0,
    z: 0,
    ease: 'power3.out',
  });
  gsap.to(controls.value.value.target, {
    delay: 0.1,
    duration: 1,
    x: 0,
    y: 0,
    z: 0,
    ease: 'power3.out',
  });
}

async function handleFrontView() {
  controls.value.value.enableDamping = false;
  controls.value.value.enabled = false;
  gsap.to(currentCamera.value.position, {
    delay: 0.1,
    duration: 1,
    x: -10,
    y: 0,
    z: 0,
    ease: 'power3.out',
    onComplete: () => {
      controls.value.value.enableDamping = true;
      controls.value.value.enabled = true;
    },
  });
  gsap.to(currentCamera.value.up, {
    delay: 0.1,
    duration: 1,
    x: 0,
    y: 0,
    z: 1,
    ease: 'power3.out',
  });
  gsap.to(controls.value.value.target, {
    delay: 0.1,
    duration: 1,
    x: 0,
    y: 0,
    z: 0,
    ease: 'power3.out',
  });
}

async function switchToPerspectiveCamera() {
  const currentUp = cameraOrthographic.value.up.clone();
  cameraOrthographicPosition.value = cameraOrthographic.value.position.clone();
  cameraPerspectivePosition.value = cameraOrthographicPosition.value;
  cameraPerspectivePosition.value = cameraOrthographicPosition.value;

  // Switch camera type
  cameraType.value = 'perspective';
  await nextTick();

  cameraPerspective.value.up = currentUp;
  cameraPerspective.value.updateProjectionMatrix();

  controls.value.value.object = cameraPerspective.value;
  controls.value.value.update();
}

async function switchToOrthographicCamera() {
  const currentUp = cameraPerspective.value.up.clone();
  cameraPerspectivePosition.value = cameraPerspective.value.position.clone();
  cameraOrthographicPosition.value = cameraPerspectivePosition.value;
  // Get matching perspective to orthographic zoom
  const distance = cameraPerspective.value.position.distanceTo(controls.value.value.target);
  const halfWidth = frustumWidthAtDistance(cameraPerspective.value, distance) / 2;
  const halfHeight = frustumHeightAtDistance(cameraPerspective.value, distance) / 2;

  // Switch camera type
  cameraType.value = 'orthographic';
  await nextTick();

  // Set matching perspective to orthographic zoom
  cameraOrthographic.value.zoom = 1;
  cameraOrthographic.value.top = halfHeight;
  cameraOrthographic.value.bottom = -halfHeight;
  cameraOrthographic.value.left = -halfWidth;
  cameraOrthographic.value.right = halfWidth;
  cameraOrthographic.value.up = currentUp;
  cameraOrthographic.value.updateProjectionMatrix();

  controls.value.value.object = cameraOrthographic.value;
  controls.value.value.update();
}

function frustumHeightAtDistance(camera, distance) {
  const vFov = (camera.fov * Math.PI) / 180;
  return Math.tan(vFov / 2) * distance * 2;
}

function frustumWidthAtDistance(camera, distance) {
  return frustumHeightAtDistance(camera, distance) * camera.aspect;
}

async function handleTransformMouseUp(object) {
  const newAnnotations = JSON.parse(JSON.stringify(annotations.value));
  const transformAnnotation = newAnnotations.find((anno) => anno.id === object.userData.annotation.id);

  transformAnnotation.data_json.x = object.position.x;
  transformAnnotation.data_json.y = object.position.y;
  transformAnnotation.data_json.z = object.position.z;
  transformAnnotation.data_json.dx *= object.scale.x;
  transformAnnotation.data_json.dy *= object.scale.y;
  transformAnnotation.data_json.dz *= object.scale.z;
  object.scale.set(1, 1, 1);

  emit('update:updatedAnnotations', JSON.parse(JSON.stringify(newAnnotations)));

  invalidateBoxes.value = true;
  nextTick(() => {
    invalidateBoxes.value = false;
  });
}

function handleCanvasClick(event) {
  if (currentTool.value === '3d-pointer') {
    // Deselect currently selected boxes
    const intersects = raycaster.value.intersectObjects(scene.value.children);
    const boxIntersects = intersects.filter((i) => i.object.type === 'Mesh' && i.object.geometry.type === 'BoxGeometry');
    if (boxIntersects.length === 0) {
      deselectAllBoxes();
    }
  }

  if (currentTool.value === '3d-box') {
    if (!editingEnabled.value) return;

    const intersects = raycaster.value.intersectObjects(scene.value.children);
    const gridIntersects = intersects.filter((i) => i.object.type === 'GridHelper');

    if (gridIntersects.length > 0 && selectedLabel.value) {
      // Create new box at average of grid intersects
      const new3DBox = {
        id: uuidv4(),
        label_name: selectedLabel.value.name,
        label_index: selectedLabel.value.index,
        label_id: selectedLabel.value.id,
        type: "3dbox",
        x: 0,
        y: 0,
        w: 0,
        h: 0,
        score: 1,
        data_json: {
          dx: 0.5,
          dy: 0.5,
          dz: 0.5,
          id: uuidv4(),
          label_name: selectedLabel.value.name,
          label_index: selectedLabel.value.index,
          x: (gridIntersects.map((i) => i.point.x).reduce((a, c) => a + c, 0)) / gridIntersects.length,
          y: (gridIntersects.map((i) => i.point.y).reduce((a, c) => a + c, 0)) / gridIntersects.length,
          z: 0,
        },
        image_id: imageObj.value.id,
      };
      emit('create-annotation', new3DBox);

      // Rerenders the boxes
      invalidateBoxes.value = true;
      nextTick(() => {
        invalidateBoxes.value = false;
      });
    }
  }
}

function disableCanvasClick() {
  const canvasEl = renderer.value.domElement;
  canvasEl.removeEventListener('click', handleCanvasClick);
}

function enableCanvasClick() {
  const canvasEl = renderer.value.domElement;
  canvasEl.addEventListener('click', handleCanvasClick);
}

function handleBoxPointerEnter(el) {
  if (['3d-pointer', '3d-edit-label', 'translate-object', 'scale-object'].includes(currentTool.value) && !el.object.userData.selected) {
    const material = el.object.material.clone();
    material.opacity = 0.375;
    el.object.material = material;
  }
}

function handleBoxPointerLeave(object) {
  if (['3d-pointer', '3d-edit-label', 'translate-object', 'scale-object'].includes(currentTool.value) && !object.userData.selected) {
    const material = object.material.clone();
    material.opacity = 0.25;
    object.material = material;
  }
}

function selectBox(object, box) {
  if (selectedAnnotationIdentifiers.value.includes(box.id)) {
    return;
  }
  // Merge into the shared cross-viewer selection instead of replacing it, so a box picked here
  // can be joined together with items already selected in the 2D canvas.
  emit('update:selectedAnnotationIdentifiers', [...selectedAnnotationIdentifiers.value, box.id]);
  object.userData.selected = true;
  const material = object.material.clone();
  material.opacity = 0.5;
  object.material = material;
}

function deselectBox(box) {
  emit('update:selectedAnnotationIdentifiers', selectedAnnotationIdentifiers.value.filter((id) => id !== box.id));
}

function deselectAllBoxes(object, box) {
  boxRefs.value.forEach((o) => {
    if (o.userData.selected) {
      o.userData.selected = false;
      const material = o.material.clone();
      material.opacity = 0.25;
      o.material = material;
    }
  });
  // Clears the whole cross-viewer selection (matching the 2D canvas' empty-space click behavior).
  emit('update:selectedAnnotationIdentifiers', []);
}

function updateBoxSelection() {
  boxRefs.value.forEach((o) => {
    if (selectedAnnotationIdentifiers.value.includes(o.userData.annotation.id)) {
      o.userData.selected = true;
      if (o.material) {
        const material = o.material.clone();
        material.opacity = 0.5;
        o.material = material;
      }
    } else {
      o.userData.selected = false;
      if (o.material) {
        const material = o.material.clone();
        material.opacity = 0.25;
        o.material = material;
      }
    }
  });
}

function deleteObjects() {
  deleteSelectedBoxes(selectedObjects.value.boxes);
}

function deleteSelectedBoxes(selectedBoxes) {
  const updatedAnnotations = annotations.value.filter((box) => !selectedBoxes.map((selectedBox) => selectedBox.id).includes(box.id));
  emit('update:updatedAnnotations', JSON.parse(JSON.stringify(updatedAnnotations)));

  // Rerenders the boxes
  invalidateBoxes.value = true;
  nextTick(() => {
    invalidateBoxes.value = false;
  });
}

</script>

<style lang="scss" scoped>

</style>
